import { terminate, clearIndexedDbPersistence, disableNetwork, enableNetwork } from 'firebase/firestore';
import { db } from './firebaseConfig';

// Track all active unsubscribe functions
let activeListeners = new Set();

export const addListener = (unsubscribe) => {
  activeListeners.add(unsubscribe);
  return unsubscribe;
};

export const removeListener = (unsubscribe) => {
  if (activeListeners.has(unsubscribe)) {
    try {
      unsubscribe();
    } catch (error) {
      console.warn('Error unsubscribing from listener:', error);
    }
    activeListeners.delete(unsubscribe);
  }
};

export const cleanupAllListeners = async () => {
  console.log('Cleaning up all Firestore listeners...');
  
  // Unsubscribe from all active listeners
  for (const unsubscribe of activeListeners) {
    try {
      unsubscribe();
    } catch (error) {
      console.warn('Error unsubscribing from listener:', error);
    }
  }
  activeListeners.clear();
  
  // Disable network to prevent new connections
  try {
    await disableNetwork(db);
  } catch (error) {
    console.warn('Error disabling network:', error);
  }
  
  // Clear indexed DB cache
  try {
    await clearIndexedDbPersistence(db);
  } catch (error) {
    console.warn('Error clearing IndexedDB:', error);
  }
  
  // Terminate Firestore instance
  try {
    await terminate(db);
  } catch (error) {
    console.warn('Error terminating Firestore:', error);
  }
};

export const resetFirestore = async () => {
  try {
    await cleanupAllListeners();
    
    // Re-enable network after cleanup
    setTimeout(async () => {
      try {
        await enableNetwork(db);
      } catch (error) {
        console.warn('Error re-enabling network:', error);
      }
    }, 1000);
    
  } catch (error) {
    console.error('Error resetting Firestore:', error);
  }
};

// Enhanced error handling wrapper for Firestore operations
export const withFirestoreErrorHandling = async (operation, fallback = null) => {
  try {
    return await operation();
  } catch (error) {
    console.error('Firestore operation failed:', error);
    
    // If it's an assertion error, try to reset and retry
    if (error.message?.includes('INTERNAL ASSERTION FAILED')) {
      console.log('Attempting to recover from assertion error...');
      await resetFirestore();
      
      if (fallback) {
        return fallback();
      }
    }
    
    throw error;
  }
};

// Setup global error handlers
if (typeof window !== 'undefined') {
  // Clean up on page unload
  window.addEventListener('beforeunload', cleanupAllListeners);
  
  // Clean up on errors
  window.addEventListener('error', (event) => {
    if (event.error?.message?.includes('FIRESTORE') && event.error?.message?.includes('ASSERTION FAILED')) {
      console.log('Detected Firestore assertion error, attempting cleanup...');
      resetFirestore();
    }
  });
  
  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    if (event.reason?.message?.includes('FIRESTORE') && event.reason?.message?.includes('ASSERTION FAILED')) {
      console.log('Detected Firestore assertion error in promise, attempting cleanup...');
      resetFirestore();
      event.preventDefault(); // Prevent the error from being logged to console
    }
  });
} 